import { isElementPartiallyInViewport, isInViewport } from '~/utils/dom';

export interface IIframeInterface {
  /** iFrame HTML instance */
  iframe: HTMLIFrameElement;
  /** iFrame was loaded after it has been added to the dom */
  iframeLoaded: boolean;
  /** iFrame was viewed at least once after it has been added to the dom */
  iframeWasViewed: boolean;
  /** iFrame window instance. Needed to receive messages or share messages with/from other widgets */
  iframeWindow: any;
  /** The skeleton preview of the iframe */
  iframeSkeleton: HTMLDivElement;
  /** Custom options for Intersection Observer */
  intersectionObserverOptions: IntersectionObserverInit;
  /** Load the iframe */
  loadIframe(): void;
  /** Unique iframe ID to create it with */
  readonly id: string;
  /** Get boolean if iframe is currently in a user view */
  readonly isInView: boolean;
  /** Add callback to queue to call once widget is loaded after it has been added to the DOM. */
  onLoad(cb: (iframeWindow: any) => void): void;
}

/**
 * Abstract class that used as a blueprint for Widgets iFrames
 * @namespace Iframe
 * @abstract
 */
export abstract class Iframe implements IIframeInterface {
  public iframe: HTMLIFrameElement;
  public iframeLoaded: boolean;
  public iframeWasViewed: boolean;
  public iframeWindow: any;
  public iframeSkeleton: HTMLDivElement;
  private onLoadCallbacks: Set<(iframeWindow: any) => void>;

  /**
   * @param {string} id - Unique iframe ID to create it with
   * @param title
   * @param {string} url - IFrame URL
   * @param {object} params - IFrame params
   * @param hasSkeleton
   * @param lazyLoad
   * @param intersectionObserverOptions
   * @throws Will throw an error if the ID or url argument is falsy.
   */
  constructor(
    public readonly id: string,
    title: string,
    readonly url: string,
    readonly params: { [key: string]: any } = {},
    protected hasSkeleton: boolean = false,
    public lazyLoad: boolean = true,
    public intersectionObserverOptions: IntersectionObserverInit = {}
  ) {
    /** Set a few guardian  */
    if (!this.id) {
      throw new Error('Iframe id is required');
    }
    if (!url) {
      throw new Error('Iframe url is required');
    }
    this.iframeLoaded = false;
    this.iframeWasViewed = false;
    this.onLoadCallbacks = new Set();
    // if (this.hasSkeleton) {
    //   // Create Iframe skeleton
    //   this.insertSkeleton();
    // }
    // Create Iframe with ID and base styles
    const iframe = document.createElement('iframe');
    const iframeUrl = `${url}?version=${params.version}`;
    // Send iframe parameters as config via postMessage

    this.onLoad((iframeWindow) => iframeWindow.postMessage({ configuration: params }, iframeUrl));
    iframe.style.width = '100%';
    iframe.style.minWidth = '100%';
    iframe.height = '0';
    iframe.name = this.id;
    iframe.id = this.id;
    iframe.title = title;
    iframe.frameBorder = '0';
    iframe.scrolling = 'no';

    this.iframe = iframe;

    if (!this.lazyLoad) {
      this.loadIframe();
    }
  }

  // protected insertSkeleton(): void {
  //   this.iframeSkeleton = document.createElement('div');
  //   this.iframeSkeleton.style.cssText = `
  //     clear: both;
  //   `;
  //   if (!document.querySelector('#iframe-keyframes-style')) {
  //     const skeletonKeyframes: HTMLStyleElement = document.createElement('style');
  //     skeletonKeyframes.id = 'iframe-keyframes-style';
  //     skeletonKeyframes.innerHTML = `
  //       @keyframes iframe-skeleton-animation {
  //         to {
  //           background-position: 100% 0, 0 0;
  //         }
  //       }
  //     `;
  //     document.head.appendChild(skeletonKeyframes);
  //   }
  // }

  /**
   * Attach the iframe url and onload listener
   */
  public loadIframe() {
    // if (!this.iframeLoaded) {
    this.iframe.addEventListener('load', () => this.iframeOnLoad());
    this.iframe.src = `${this.url}?version=${this.params.version}`;
    // }
  }

  /**
   * Checks if element is in view currently
   * @returns boolean
   */
  get isInView(): boolean {
    if (isElementPartiallyInViewport(this.iframe) || isInViewport(this.iframe)) {
      this.iframeWasViewed = true;
      return true;
    }
    return false;
  }

  /**
   * Add callback to queue to call once widget is loaded after it has been added to the DOM.
   * @param {Function} cb - callback function which should be fired on iframe load
   * @returns {void}
   */
  public onLoad = (cb: (iframeWindow: any) => void): void => {
    if (typeof cb !== 'function') {
      return;
    }

    // ⬇ Add callback to queue
    if (typeof cb === 'function') {
      this.onLoadCallbacks.add(cb);
    }

    // ⬇ Call callback instantly if iFrame is already loaded
    if (this.iframeLoaded) {
      return cb(this.iframeWindow);
    }
  };

  /**
   * Iframe onload handler which will call onload callbacks once iFrame loaded
   * @returns {void}
   */
  protected iframeOnLoad = (): void => {
    this.iframeWindow = this.iframe.contentWindow;
    this.iframeLoaded = true;
    this.iframeSkeleton?.remove();
    if (this.onLoadCallbacks.size > 0) {
      this.onLoadCallbacks.forEach((callback) => callback(this.iframeWindow));
    }
  };
}

export default Iframe;
